"use client";

import { useRef, useState } from "react";

/**
 * Pointer-based drag-to-reorder for a vertical list: the dragged item follows
 * the pointer via a transform while its neighbours slide out of the way;
 * dropping commits the move through `onReorder`. Same mechanics as the
 * automation builder's action cards.
 *
 * Attach `scrollerRef` to the list container, spread `itemProps(key, index)`
 * on each item wrapper, and put `onPointerDown={startDrag(key)}` on the handle.
 */
export function useSlidingReorder<K extends string | number>({
  keys,
  onReorder,
  gap = 0,
  clampIndex,
}: {
  /** Current item keys in render order. */
  keys: K[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  /** Vertical gap between items, px (matches the container's `gap-*`). */
  gap?: number;
  /** Restrict where an item may land, e.g. never above a leading header. */
  clampIndex?: (index: number, key: K) => number;
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef(new Map<K, HTMLElement>());
  const [dragKey, setDragKey] = useState<K | null>(null);
  const [dragDelta, setDragDelta] = useState(0);
  const [overIndex, setOverIndex] = useState(0);
  const dragMetaRef = useRef<{ index: number; height: number } | null>(null);

  const startDrag = (key: K) => (e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    e.preventDefault();

    const sRect = scroller.getBoundingClientRect();
    const toContent = (clientY: number) => clientY - sRect.top + scroller.scrollTop;
    // snapshot in scroller-content coordinates so it stays valid under auto-scroll
    const items = keys.map((k, index) => {
      const r = itemRefs.current.get(k)?.getBoundingClientRect();
      return { key: k, index, top: r ? toContent(r.top) : 0, height: r?.height ?? 0 };
    });
    const me = items.find((it) => it.key === key);
    const first = items[0];
    const last = items[items.length - 1];
    if (!me || !first || !last || items.length < 2) return;
    const inRange = (index: number) => Math.min(items.length - 1, Math.max(0, index));
    const lowest = items[clampIndex ? inRange(clampIndex(0, key)) : 0] ?? first;
    const highest = items[clampIndex ? inRange(clampIndex(items.length - 1, key)) : items.length - 1] ?? last;
    const minDelta = lowest.top - me.top;
    const maxDelta = highest.top + highest.height - me.height - me.top;

    dragMetaRef.current = { index: me.index, height: me.height };
    setDragKey(key);
    setDragDelta(0);
    setOverIndex(me.index);

    const startY = toContent(e.clientY);
    let lastClientY = e.clientY;
    let currentOver = me.index;
    let raf = 0;

    const update = () => {
      const contentY = lastClientY - sRect.top + scroller.scrollTop;
      const delta = Math.min(Math.max(contentY - startY, minDelta), maxDelta);
      setDragDelta(delta);
      // direction-aware edges: a tall item can still pass a short one at the clamped ends
      const top = me.top + delta;
      const bottom = top + me.height;
      let idx = me.index;
      for (const it of items) {
        if (it.key === key) continue;
        const center = it.top + it.height / 2;
        if (it.index > me.index && bottom > center) idx = Math.max(idx, it.index);
        if (it.index < me.index && top < center) idx = Math.min(idx, it.index);
      }
      if (clampIndex) idx = clampIndex(idx, key);
      currentOver = idx;
      setOverIndex(idx);
    };

    const EDGE = 48;
    const autoScroll = () => {
      let dy = 0;
      if (lastClientY < sRect.top + EDGE) dy = -Math.ceil((sRect.top + EDGE - lastClientY) / 6);
      else if (lastClientY > sRect.bottom - EDGE) dy = Math.ceil((lastClientY - (sRect.bottom - EDGE)) / 6);
      if (dy !== 0) {
        const before = scroller.scrollTop;
        scroller.scrollTop = before + dy;
        if (scroller.scrollTop !== before) update();
      }
      raf = requestAnimationFrame(autoScroll);
    };
    raf = requestAnimationFrame(autoScroll);

    const onMove = (ev: PointerEvent) => {
      lastClientY = ev.clientY;
      update();
    };
    const finish = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (me.index !== currentOver) onReorder(me.index, currentOver);
      setDragKey(null);
      setDragDelta(0);
      dragMetaRef.current = null;
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
  };

  const transformFor = (key: K, index: number): string | undefined => {
    const meta = dragMetaRef.current;
    if (dragKey == null || !meta) return undefined;
    if (key === dragKey) return `translateY(${dragDelta}px)`;
    const shift = meta.height + gap;
    if (meta.index < overIndex && index > meta.index && index <= overIndex) return `translateY(-${shift}px)`;
    if (meta.index > overIndex && index >= overIndex && index < meta.index) return `translateY(${shift}px)`;
    return undefined;
  };

  /** Wrapper props for item `key` at `index`: ref, slide transition, transform. */
  const itemProps = (key: K, index: number) => ({
    ref: (el: HTMLElement | null) => {
      if (el) itemRefs.current.set(key, el);
      else itemRefs.current.delete(key);
    },
    className: dragKey === key ? "relative z-10 shadow-lg" : dragKey != null ? "transition-transform duration-150" : "",
    style: { transform: transformFor(key, index) },
  });

  return { scrollerRef, startDrag, itemProps, dragKey };
}
